/* eslint-disable max-lines */

import { NgTemplateOutlet } from '@angular/common';
import type { Signal } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  ViewChild,
  ViewContainerRef,
  computed,
  input,
  signal,
  output,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';

import { animationFrameScheduler, defer, fromEvent, merge } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { StageCategoryColorIndicatorComponent } from '@core/offers-ui';
import {
  EMPTY_VALUE,
  NONE_VALUE,
  PIVOT_TABLE_COUNT_ID,
  PivotTableData,
  PivotTableDataValueRow,
  PivotTableRowValue,
  ReportDashboardComponentViewData,
  ReportsDataPivotTableRow,
  VisualizationData,
  injectReportVisualizationOptions,
} from '@core/reports-types';
import type { DataTableColumn } from '@core/user-interface-legacy';
import {
  DataTableComponent,
  IconComponent,
  PaginationComponent,
  PaginationCounterComponent,
  ResizeObserverEntryDirective,
  ToolbarComponent,
  ToolbarSecondaryComponent,
  TruncateComponent,
} from '@core/user-interface-legacy';
import { deepEqual, generateUuid, injectUntilDestroyed } from '@core/utils-legacy';

import { TablePaginationPipe } from '../table/table-pagination/table-pagination.pipe';

export type Pagination = {
  pagesCount: number;
  limit: number;
  page: number;
  start: number;
  end: number;
  total: number;
};

const tableColWidth = 200;
const ROWS_PER_PAGE = 10;

type PivotTableSorting = {
  primarySort: {
    id: string;
    sort: 'asc' | 'desc';
  } | null;
  secondarySort: {
    id: string;
    sort: 'asc' | 'desc';
  } | null;
};

@Component({
  selector: 'reports-dashboards-pivot-table',
  templateUrl: './pivot-table.component.html',
  styleUrls: ['../table/shared-table-style.less', './pivot-table.component.less'],
  imports: [
    IconComponent,
    DataTableComponent,
    ToolbarComponent,
    ToolbarSecondaryComponent,
    PaginationCounterComponent,
    PaginationComponent,
    NgTemplateOutlet,
    TablePaginationPipe,
    TruncateComponent,
    StageCategoryColorIndicatorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[class.fullscreen]': 'fullscreenEnabled()',
  },
})
export class ReportDashboardsPivotTableComponent {
  private readonly resizeObserverEntry = inject(ResizeObserverEntryDirective);
  private readonly options = injectReportVisualizationOptions({
    optional: true,
  });

  protected readonly cdRef = inject(ChangeDetectorRef);

  public readonly vcRef = inject(ViewContainerRef);
  public readonly i18n = inject(I18nService);

  @Input({
    required: true,
    alias: 'data',
  })
  public set _data(data: VisualizationData<PivotTableData>) {
    // Do not redraw table when params change, until new data is fetched
    if (data.dataset.isInitial) {
      return;
    }
    this.currentPage.set(1);
    this.data.set(data);
  }
  public data = signal<VisualizationData<PivotTableData> | null>(null);

  public component = input.required<ReportDashboardComponentViewData>();
  public fullscreenEnabled = input<boolean>(false);
  public resourcesPreviewAvailable = input.required<boolean>();
  public isExport = input.required<boolean>();

  public openResourceAt = output<[number, number]>();
  public tableSortChange = output<DataTableColumn>();

  public dataTableColumnsMapped: Signal<DataTableColumn[]> = computed(() => {
    const { primarySort, secondarySort } = this.sorting();
    const data = this.data();
    const isExport = this.isExport();
    if (!data) {
      return [];
    }
    const sharedSettings = {
      geometry: {
        width: tableColWidth,
        minWidth: tableColWidth,
      },
      options: {
        draggable: false,
        resizable: false,
        removable: false,
        sortable: this.options?.tableSortingAvailable ?? false,
      },
    };

    return [
      {
        ...sharedSettings,
        id: `${data.vis.dimensions[0].id}`,
        label: data.vis.dimensions[0].name,
        sort: !isExport && primarySort?.id === data.vis.dimensions[0].id ? primarySort.sort : null,
      },
      {
        ...sharedSettings,
        id: `${data.vis.dimensions[1].id}`,
        label: data.vis.dimensions[1].name,
        sort: !isExport ? (secondarySort?.sort ?? null) : null,
      },
      {
        ...sharedSettings,
        id: PIVOT_TABLE_COUNT_ID,
        label: data.vis.dimensions[2].name,
        sort: !isExport && primarySort?.id === PIVOT_TABLE_COUNT_ID ? primarySort.sort : null,
      },
    ];
  });

  // eslint-disable-next-line max-lines-per-function
  public dataTableRowsMapped: Signal<ReportsDataPivotTableRow[]> = computed(() => {
    let data = this.dataTableRowsSource();
    // primary sort is the sort on all rows, secondary sort is sort inside rows
    const { primarySort, secondarySort } = this.sorting();

    const emptySort = (
      id: string,
      a: PivotTableRowValue[number],
      b: PivotTableRowValue[number],
    ): number => {
      if (a.id === EMPTY_VALUE || a.id === NONE_VALUE) {
        return 1;
      }
      if (b.id === EMPTY_VALUE || b.id === NONE_VALUE) {
        return -1;
      }
      return 0;
    };

    if (primarySort) {
      const sortSign = primarySort.sort === 'asc' ? 1 : -1;
      const sortId = primarySort.id;
      const sort = (a: string | number, b: string | number) => (a > b ? 1 : -1) * sortSign;

      data = data.sort((a, b) => {
        // for count we sort by all entries in the row
        if (sortId === PIVOT_TABLE_COUNT_ID) {
          const aSum = a.values[sortId].reduce((acc, item) => acc + <number>item.value!, 0);
          const bSum = b.values[sortId].reduce((acc, item) => acc + <number>item.value!, 0);
          return sort(aSum, bSum);
        }
        const aValue = a.values[sortId][0];
        const bValue = b.values[sortId][0];
        const emptySortResult = emptySort(sortId, aValue, bValue);
        if (emptySortResult !== 0) {
          return emptySortResult;
        }
        return sort(aValue.formatted ?? aValue.name!, bValue.formatted ?? bValue.name!);
      });
    }

    // With secondary sort, in every row we need to sort values in the same order, as the secondary row (middle)
    // To achieve that we use map sort method
    if (secondarySort) {
      const sortSign = secondarySort.sort === 'asc' ? 1 : -1;
      const mapped = data[0].values[secondarySort.id]
        .map((value, index) => ({ value, index }))
        .sort((a, b) => {
          const emptySortResult = emptySort(secondarySort.id, a.value, b.value);
          if (emptySortResult !== 0) {
            return emptySortResult;
          }
          const aName = a.value.formatted ?? a.value.name!;
          const bName = b.value.formatted ?? b.value.name!;
          return aName.localeCompare(bName) * sortSign;
        })
        .map(({ index }) => index);

      data = data.map(row => {
        const values = Object.entries(row.values).reduce(
          (acc, [key, values]) => {
            if (values.length !== mapped.length) {
              return {
                ...acc,
                [key]: values,
              };
            }
            return {
              ...acc,
              [key]: mapped.map(index => values[index]),
            };
          },
          <Record<string, PivotTableRowValue>>{},
        );
        return {
          ...row,
          values,
        };
      });
    }

    return data;
  });

  public currentPage = signal(1);
  public pagination: Signal<Pagination> = computed(() => {
    const page = this.currentPage();
    const rows = this.dataTableRowsMapped();
    const limit = this.fullscreenEnabled() ? rows.length : ROWS_PER_PAGE;
    return {
      page,
      pagesCount: Math.ceil(rows.length / limit),
      start: (page - 1) * limit + 1,
      end: (page - 1) * limit + rows.slice((page - 1) * limit, (page - 1) * limit + limit).length,
      total: rows.length,
      limit: limit,
    };
  });

  public shadows = signal({ left: false, right: false });

  @ViewChild('scrollContainer', { read: ElementRef }) public scrollContainer: ElementRef;

  // primary sort is the sort on all rows, secondary sort is sort of entries inside each row
  private sorting = computed(() => {
    const component = this.component();
    let sorting: PivotTableSorting = {
      primarySort: null,
      secondarySort: null,
    };
    if (component.sortBy && component.sortOrder) {
      sorting = {
        ...sorting,
        primarySort: {
          id: component.sortBy,
          sort: component.sortOrder,
        },
      };
    }
    if (component.settings?.localSortBy && component.settings?.localSortOrder) {
      sorting = {
        ...sorting,
        secondarySort: {
          id: component.settings.localSortBy,
          sort: component.settings.localSortOrder,
        },
      };
    }
    return sorting;
  });

  // unsorted data
  private dataTableRowsSource: Signal<ReportsDataPivotTableRow[]> = computed(() => {
    const data = this.data();
    const columnsMappped = this.dataTableColumnsMapped();
    if (!data) {
      return [];
    }

    const valuesReduce = (
      row: PivotTableDataValueRow,
    ): Record<number | string, PivotTableRowValue> =>
      row.reduce(
        (acc, item, index: number) => {
          const columnId = columnsMappped[index].id;
          const value = Array.isArray(item) ? item : [item];
          acc[columnId] = value.map((x, originalIndex) => ({
            ...x,
            isClickable: columnId === PIVOT_TABLE_COUNT_ID,
            originalIndex,
            id: (<any>x).id ?? generateUuid(),
          }));
          return acc;
        },
        <Record<string | number, PivotTableRowValue>>{},
      );

    return data.vis.values.map((row, index) => ({
      index,
      values: valuesReduce(row),
      id: generateUuid(),
    }));
  });

  private untilDestroyed = injectUntilDestroyed();

  public changePage(page: number): void {
    this.currentPage.set(page);
    this.cdRef.detectChanges();
  }

  public ngAfterViewInit() {
    const width$ = this.resizeObserverEntry.resized.pipe(
      map(({ width }) => width),
      // any emmision will do
      startWith(this.resizeObserverEntry.entry?.width),
      distinctUntilChanged(),
    );

    const scroll$ = defer(() => fromEvent(this.scrollContainer.nativeElement, 'scroll'));

    merge(width$, scroll$)
      .pipe(
        debounceTime(1, animationFrameScheduler),
        map(() => this.scrollContainer?.nativeElement),
        filterBoolean,
        map(scrollContainer => {
          const { scrollWidth = 0, scrollLeft = 0, offsetWidth = 0 } = scrollContainer;
          const left = scrollLeft > 0;
          const right = scrollLeft + offsetWidth < scrollWidth;
          return { left, right };
        }),
        distinctUntilChanged(deepEqual),
        this.untilDestroyed,
      )
      .subscribe(data => {
        this.shadows.set(data);
        this.cdRef.detectChanges();
      });
  }

  public toggleColumnSort(column: DataTableColumn): void {
    const nextSort: 'asc' | 'desc' = column.sort === 'desc' ? 'asc' : 'desc';
    this.tableSortChange.emit({
      ...column,
      sort: nextSort,
    });
  }

  public isClickable(value: PivotTableRowValue[0]): boolean {
    return (
      this.resourcesPreviewAvailable() &&
      value.isClickable &&
      typeof value.value === 'number' &&
      value.value > 0
    );
  }

  public openResource(rowIndex: number, itemIndex: number) {
    this.openResourceAt.emit([rowIndex, itemIndex]);
  }

  // TODO
}
